import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, Signal, WritableSignal, computed, inject, signal, Inject, PLATFORM_ID, input, viewChild } from '@angular/core';
import { DeliveryAddress } from '../signup.types';
import { AddressService } from '../../../shared/address.service';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatStep } from '@angular/material/stepper';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { ArrayOptionsItem, DeliveryDay, DeliveryOptionsResponseItem, PickUpOption } from '../../../shared/types/account.types';
import { AccountService } from '../../../shared/account.service';
import { AutocompleteInputComponent } from '../autocomplete-input/autocomplete-input.component';
import { LocalStorageService } from '../../../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../../shared/constants/databases';
import { tap } from 'rxjs';
import { formatDateToReadableString } from '../../../shared/utils/formatting';
import { toObservable } from '@angular/core/rxjs-interop';
import { DataLayerService } from '../../../shared/data-layer/data-layer.service';
import { DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS, DataLayer_SignUpFunnel_Pickup } from '../../../shared/data-layer/data-layer.types';

@Component({
  selector: 'app-check-address-signup',
  providers: [],
  imports: [
    CommonModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatRadioModule,
    AutocompleteInputComponent,
  ],
  templateUrl: './check-address-signup.component.html',
  styleUrl: './check-address-signup.component.scss'
})
export class CheckAddressSignupComponent implements OnInit {
  private localStorageService = inject(LocalStorageService)
  private zone = inject(NgZone);
  private addressService = inject(AddressService);
  private signalsStoreService = inject(SignalsStoreService);
  private accountService = inject(AccountService);
  #dataLayerService = inject(DataLayerService);

  @Input() firstStep!: MatStep;
  isDirectSignup = input(false);
  directSignupMembershipId = input<number | null>(null);
  @Output() onNextStep: EventEmitter<any> = new EventEmitter<any>();
  @Output() deliveryAddressChanged = new EventEmitter<WritableSignal<Partial<DeliveryAddress>>>();
  @Output() selectedDayChanged = new EventEmitter<any>();
  @Output() selectedTimeWindowChanged = new EventEmitter<any>();
  @Output() deliveryOptionChanged = new EventEmitter<any>();
  @Output() emailChanged = new EventEmitter<string>();
  @Output() emailAlreadyInUse = new EventEmitter<string>();
  @Output() pickupInfoChanged = new EventEmitter<DataLayer_SignUpFunnel_Pickup>();

  emailInput = viewChild<ElementRef>('emailInput');

  #EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,6}$/;

  email = signal('');
  emailTouched = signal(false);
  emailValid = computed(() => {
    if (!this.emailTouched()) return true;
    return this.#EMAIL_PATTERN.test(this.email());
  });
  hasValidEmail = computed(() => this.#EMAIL_PATTERN.test(this.email()));

  deliveryAddress: WritableSignal<Partial<DeliveryAddress>> = signal({
    additionalAddress: '',
    address: '',
    city: '',
    deliveryNotes: '',
    stateCode: '',
    zipCode: '',
  })

  deliveryOption: any[] = [];
  deliveryOptionSelected: WritableSignal<any> = signal(null);
  deliveryOptions$ = toObservable(this.accountService.getDeliveryOptions);

  validSelectedAddress = signal<any>(null);

  //#region Pickup Flow
  pickUpOptions: PickUpOption[] = [];
  selectedPickUpOption: WritableSignal<PickUpOption | null> = signal(null);
  pickUpDeliveryDays: DeliveryDay[] = [];
  selectedPickUpDeliveryDay: WritableSignal<any> = signal(null);
  zoneId: number = 0;
  //#endregion

  //#region Check Address Flow
  selectedDeliveryDay: WritableSignal<DeliveryOptionsResponseItem | null> = signal(null);
  checkAddressDeliveryDays: DeliveryOptionsResponseItem[] = [];
  timeSet: Signal<ArrayOptionsItem[]> = computed(() => this.setUpTimeSet());
  selectedDeliveryTimeWindow: any = signal(null);
  //#endregion


  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.deliveryOptions$.pipe(
      tap((deliveryOptions: DeliveryOptionsResponseItem[]) => {

        if (!deliveryOptions?.length) return;

        this.checkAddressDeliveryDays = deliveryOptions;

        if (this.checkAddressDeliveryDays.length) {

          if (this.checkAddressDeliveryDays[0].deliveryDate) {
            const { dayName, month, day } = formatDateToReadableString(this.checkAddressDeliveryDays[0].deliveryDate);
            this.checkAddressDeliveryDays[0].formattedDate = `${dayName}, ${month} ${day}`;
          }

          this.selectedDeliveryDay.set(this.checkAddressDeliveryDays[0]);
          this.selectedDeliveryTimeWindow.set(null)
        }
      })
    ).subscribe();
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.updateDeliverySelection();
    this.getAllDeliveryOptions();
    this.addressService.setFirstPickUpMembershipSelected$.pipe(tap((value) => value ? this.selectFirstPickUpMembership() : null)).subscribe();
  }

  private getAllDeliveryOptions() {
    this.accountService.getAllCustomerTypes().subscribe(deliveryOption => {
      this.deliveryOption = deliveryOption.data;

      let selectedDeliveryOption;
      const mid = this.directSignupMembershipId();
      if (this.isDirectSignup() && mid) {
        selectedDeliveryOption = this.deliveryOption.find(d => d.id === mid);
      }

      if (!selectedDeliveryOption) {
        const storedDeliveryOptionSelected: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE);
        const signupSelectedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP);
        selectedDeliveryOption = this.deliveryOption.find(d => d.id === storedDeliveryOptionSelected?.id || d.id === signupSelectedMembership?.id) || this.deliveryOption[0];
      }

      this.deliveryOptionSelected.set(selectedDeliveryOption);
      if (!this.deliveryOptionSelected()?.configuration?.requireAddress)
        this.getPickupOptions(this.deliveryOptionSelected())
    });
  }

  getPickupOptions(membership: any) {
    if (!membership?.id) return;
    this.accountService.getPickUpOption(membership.id).subscribe(pickUpOptions => {
      this.pickUpOptions = pickUpOptions.map((l: { composedName: string; name: any; locationAddress: any; }) => {
        l.composedName = `${(l.name ? `${l.name} - ` : '') || ''}${l.locationAddress}`;
        return l;
      });
      const storedPickUpLocation: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SELECTED_PICKUP_OPTION);
      if (storedPickUpLocation)
        this.setSelectedPickUpOption({ value: storedPickUpLocation.id })

      if (this.pickUpOptions.length === 1)
        this.setSelectedPickUpOption({ value: this.pickUpOptions[0].id });
    });
  }

  updateDeliveryAddress(newValue: any) {
    this.deliveryAddress.update((value) => ({ ...value, ...newValue }))
  }

  setSelectedDeliveryDay(event: MatSelectChange) {
    const option = this.checkAddressDeliveryDays.find(o => o.id === event.value);
    if (option) {
      this.selectedDeliveryDay.set(option);
      this.selectedDeliveryTimeWindow.set(null)
    }
  }

  setSelectedPickUpOption(event: any) {
    this.selectedPickUpDeliveryDay.set(null)
    const option = this.pickUpOptions.find(o => o.id === event.value);
    if (option) {
      this.selectedPickUpOption.set(option);
      this.zoneId = option.id
      this.pickUpDeliveryDays = option.deliveryDays;
    }
  }

  setSelectedPickUpDeliveryDays(event: MatSelectChange) {
    const option = this.pickUpDeliveryDays.find(o => o.id === event.value);
    if (option) {
      this.selectedPickUpDeliveryDay.set({ ...option, deliveryDay: option.id, zoneId: this.zoneId });
    }
  }

  public updateDeliverySelection(restart: boolean = false, loadPickups: boolean = false) {
    if (restart) {
      this.validSelectedAddress.set(null);
    }
    this.updateDeliveryAddress({ 'deliveryOptionSelected': this.deliveryOptionSelected() });
    this.selectedPickUpDeliveryDay.set(null);
  }

  private setUpTimeSet() {
    const selectedDay = this.selectedDeliveryDay();
    return (selectedDay && selectedDay.timeSet && selectedDay.timeSet.length) ? [{ id: -1, name: "No preferences" }, ...selectedDay.timeSet] : [];
  }

  shouldDisabledPickUpFlow() {
    return !this.selectedPickUpDeliveryDay() || !this.selectedPickUpOption() || !this.hasValidEmail();
  }

  shouldDisableCheckAddresFlow() {
    return !this.selectedDeliveryDay() || !this.deliveryOptionSelected() || !this.validSelectedAddress()
      || this.deliveryAddress().deliveryNotes == '' || !this.hasValidEmail();
  }

  onEmailChange(value: string) {
    this.email.set(value);
    this.emailChanged.emit(value);
  }

  validateEmail() {
    this.emailTouched.set(true);
    const emailValue = this.email();
    if (!this.#EMAIL_PATTERN.test(emailValue)) return;

    this.accountService.validateEmailExistence(emailValue).subscribe(emailUsed => {
      if (emailUsed) {
        this.emailAlreadyInUse.emit(emailValue);
      }
    });
  }

  clearAndFocusEmail() {
    this.email.set('');
    this.emailTouched.set(false);
    this.emailChanged.emit('');
    this.emailInput()?.nativeElement?.focus();
  }

  continue() {
    this.firstStep.completed = true
    this.deliveryAddressChanged.emit(this.deliveryAddress);

    if (this.deliveryOptionSelected()?.configuration?.requireAddress) {
      const nav = this.addressService.navegoAddress();
      if (nav) {
        this.#dataLayerService.trackSignUpFunnelDeliveryAddressEvent({
          funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_information,
          funnel_step_number: 1,
          delivery_type: 'delivery',
          delivery: {
            address: nav.street,
            city: nav.city,
            state: nav.state,
            zip_code: this.deliveryAddress().zipCode ?? '',
            zone: nav.zoneName,
            in_area: true,
          },
          user: { email: this.email() },
        });
      }
      this.selectedDayChanged.emit(this.selectedDeliveryDay());
      this.selectedTimeWindowChanged.emit(this.selectedDeliveryTimeWindow());
    } else {
      // WH also sells through Pick-up memberships, so the funnel fires here too,
      // carrying a pickup object instead of the address-oriented delivery block.
      const pickup = this.#getPickupInfo();
      this.pickupInfoChanged.emit(pickup);
      this.#dataLayerService.trackSignUpFunnelDeliveryAddressEvent({
        funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_information,
        funnel_step_number: 1,
        delivery_type: 'pickup',
        pickup,
        user: { email: this.email() },
      });
      this.selectedDayChanged.emit(this.selectedPickUpDeliveryDay());
      this.selectedTimeWindowChanged.emit(-1); // Pickup locations doesn't requires time window for ME
    }

    this.deliveryOptionChanged.emit(this.deliveryOptionSelected());
    this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP, this.deliveryOptionSelected());
    this.onNextStep.emit(this.firstStep);
  }

  selectFirstPickUpMembership() {
    const selected = this.deliveryOption.find(m => !m.configuration?.requireAddress);
    this.deliveryOptionSelected.set(selected);
    this.getPickupOptions(this.deliveryOptionSelected())
  }

  onValidDeliveryAddressSelected(e: any) {
    this.validSelectedAddress.set(e);
    this.updateDeliveryAddress({ zipCode: e?.zipCode ?? '' });
  }

  #getPickupInfo(): DataLayer_SignUpFunnel_Pickup {
    const option: any = this.selectedPickUpOption();
    const day: any = this.selectedPickUpDeliveryDay();
    return {
      location: option?.composedName ?? option?.locationAddress ?? '',
      day: day?.name ?? '',
      zone: String(day?.zoneId ?? ''),
    };
  }

}
